import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import { waLink, DEFAULT_WA_MESSAGE } from "../lib/whatsapp";

const msgInput =
  "w-full border-b border-white/20 bg-transparent py-3 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-gold";

const MessageCard = () => {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
    } catch {
      setError("Une erreur est survenue, merci de réessayer.");
    }
  };

  return (
    <motion.div
      data-testid="testimonial-cta-card"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
      className="grain relative flex flex-col overflow-hidden rounded-3xl bg-[#0a0a0a] p-8 text-white transition-transform duration-300 hover:-translate-y-1.5"
    >
      {sent ? (
        <div data-testid="message-success">
          <p className="font-display text-2xl leading-snug tracking-tight">
            Message <em className="italic text-gold">bien reçu !</em>
          </p>
          <p className="mt-4 text-sm leading-relaxed text-white/60">
            Merci pour votre message — nous vous répondons très rapidement.
          </p>
        </div>
      ) : open ? (
        <form onSubmit={submit} className="flex flex-1 flex-col gap-4" data-testid="message-form">
          <p className="font-display text-2xl leading-snug tracking-tight">
            Écrivez<em className="italic text-gold">-nous.</em>
          </p>
          <input
            type="text"
            value={form.name}
            onChange={set("name")}
            required
            placeholder="Votre nom"
            data-testid="message-input-name"
            className={msgInput}
          />
          <input
            type="text"
            value={form.contact}
            onChange={set("contact")}
            placeholder="Email ou WhatsApp (pour vous répondre)"
            data-testid="message-input-contact"
            className={msgInput}
          />
          <textarea
            value={form.message}
            onChange={set("message")}
            required
            rows={3}
            placeholder="Votre message…"
            data-testid="message-input-message"
            className={`${msgInput} resize-none`}
          />
          {error && (
            <p className="text-xs text-red-400" data-testid="message-error">{error}</p>
          )}
          <div className="mt-auto flex items-center gap-4 pt-2">
            <button
              type="submit"
              data-testid="message-submit-button"
              className="rounded-full bg-gold px-7 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03]"
            >
              Envoyer
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="message-cancel-button"
              className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50 transition-colors hover:text-white"
            >
              Annuler
            </button>
          </div>
        </form>
      ) : (
        <>
          <p className="font-display text-2xl leading-snug tracking-tight">
            Votre événement mérite <em className="italic text-gold">le même soin.</em>
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="message-open-button"
            className="group mt-8 flex items-center gap-2 self-start text-xs font-bold uppercase tracking-[0.25em] text-gold"
          >
            Écrivez-nous
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </>
      )}
    </motion.div>
  );
};

const moreTestimonials = [
  {
    quote: "Un vin d'honneur spécial servi avec le sourire. Un service très professionnel et très élégant.",
    label: "Vin d'honneur",
    place: "Île-de-France",
    testid: "testimonial-vin",
  },
  {
    quote: "Nous avons fait plusieurs événements avec FK Conseil : rien à dire, un service de très haut niveau. Merci à l'initiateur.",
    label: "Événements privés",
    place: "France",
    testid: "testimonial-evenements",
  },
  {
    quote: "J'avais perdu vos contacts… vous êtes une équipe magnifique.",
    label: "Mariage",
    place: "Genève, Suisse — Décembre 2025",
    testid: "testimonial-geneve",
  },
  {
    quote: "Votre participation à nos conférences et à notre mariage en Belgique était somptueuse. Vous êtes très flexibles et pragmatiques — merci les gars !",
    label: "Conférences & mariage",
    place: "Belgique",
    testid: "testimonial-belgique",
  },
  {
    quote: "Le mariage de maman Jeannette à Lausanne était simplement top : très élégant, professionnel, un service haut de gamme niveau palace 5 étoiles. Au nom des mariés, nous vous remercions, vous et votre équipe.",
    label: "Mariage",
    place: "Lausanne, Suisse",
    testid: "testimonial-lausanne",
  },
];

export const Temoignages = () => (
  <section id="avis" data-testid="testimonials-section" className="bg-[#f9f8f6] px-6 py-28 text-[#0a0a0a] lg:px-10 lg:py-40">
    <div className="mx-auto max-w-5xl">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-6 text-center text-[11px] font-semibold uppercase tracking-[0.4em] text-[#aa8c2c]"
      >
        Avis clients
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.1 }}
        className="mb-20 text-center font-display text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl"
      >
        Ils nous ont fait <em className="italic text-[#aa8c2c]">confiance.</em>
      </motion.h2>
      <motion.blockquote
        data-testid="testimonial-baccara"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className="relative rounded-3xl border border-[#0a0a0a]/10 bg-white p-10 text-center shadow-xl lg:p-16"
      >
        <span className="absolute -top-6 left-1/2 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full bg-gold text-[#0a0a0a]">
          <Quote className="h-5 w-5" />
        </span>
        <div className="mb-8 flex justify-center gap-1.5" data-testid="testimonial-stars">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-5 w-5 fill-[#d4af37] text-[#d4af37]" />
          ))}
        </div>
        <p className="mx-auto max-w-3xl font-display text-2xl leading-relaxed tracking-tight sm:text-3xl">
          « Un mariage exceptionnel avec une équipe professionnelle.
          <em className="italic text-[#aa8c2c]"> J'ai recommandé</em> FK Conseil &amp; Événement
          à tous mes proches. »
        </p>
        <footer className="mt-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#0a0a0a]">Mariage — Salle Baccara</p>
          <p className="mt-2 text-xs uppercase tracking-[0.25em] text-[#0a0a0a]/40">25 juillet 2026</p>
        </footer>
      </motion.blockquote>
      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {moreTestimonials.map((t, i) => (
          <motion.blockquote
            key={t.testid}
            data-testid={t.testid}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col rounded-3xl border border-[#0a0a0a]/10 bg-white p-8 shadow-md transition-transform duration-300 hover:-translate-y-1.5"
          >
            <div className="mb-5 flex gap-1">
              {[...Array(5)].map((_, s) => (
                <Star key={s} className="h-3.5 w-3.5 fill-[#d4af37] text-[#d4af37]" />
              ))}
            </div>
            <p className="flex-1 text-sm leading-relaxed text-[#0a0a0a]/70">« {t.quote} »</p>
            <footer className="mt-6 border-t border-[#0a0a0a]/10 pt-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#0a0a0a]">{t.label}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#0a0a0a]/40">{t.place}</p>
            </footer>
          </motion.blockquote>
        ))}
        <MessageCard />
      </div>
    </div>
  </section>
);
